import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { logout } from "../utils/auth";
import { fetchSurveySubmission } from "../utils/api";
import DashboardSidebar from "../components/DashboardSidebar";

const diseaseModulePrefixes = ["lc_", "mecfs_", "pots_", "mcas_", "sjogren_", "eds_", "migraine_"];

function formatLabel(key) {
  return key
    .replace(/^sym_/, "")
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\bsev\b/i, "severity")
    .replace(/\bfreq\b/i, "frequency")
    .replace(/\bhr\b/g, "HR")
    .replace(/\bpem\b/g, "PEM")
    .replace(/\bmecfs\b/i, "ME/CFS")
    .replace(/\bmcas\b/i, "MCAS")
    .replace(/\beds\b/i, "EDS")
    .replace(/\blc\b/i, "Long COVID")
    .replace(/\bsjogren\b/i, "Sjogren")
    .replace(/\b([a-z])/g, (match) => match.toUpperCase());
}

function normalizeValue(value) {
  if (Array.isArray(value)) {
    return value.length ? value.join(", ") : null;
  }

  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === "number" && value < 0) {
    return null;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  return String(value);
}

function safeParseSurveyJson(value) {
  if (!value) {
    return {};
  }

  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function buildSections(surveyData) {
  const symptomEntries = [];
  const diseaseModuleEntries = [];
  const generalEntries = [];

  Object.entries(surveyData).forEach(([key, rawValue]) => {
    const value = normalizeValue(rawValue);
    if (value === null) {
      return;
    }

    const entry = { key, label: formatLabel(key), value };

    if (key.startsWith("sym_")) {
      symptomEntries.push(entry);
      return;
    }

    if (diseaseModulePrefixes.some((prefix) => key.startsWith(prefix))) {
      diseaseModuleEntries.push(entry);
      return;
    }

    generalEntries.push(entry);
  });

  return [
    { title: "Survey Overview", entries: generalEntries },
    { title: "Symptoms", entries: symptomEntries },
    { title: "Disease Modules", entries: diseaseModuleEntries },
  ].filter((section) => section.entries.length > 0);
}

export default function SurveyResultPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [activeSection, setActiveSection] = useState("All");

  useEffect(() => {
    let cancelled = false;

    async function loadSubmission() {
      setLoading(true);
      setError("");

      try {
        const data = await fetchSurveySubmission(id);
        if (!cancelled) {
          setSubmission(data);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Failed to load the saved survey.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadSubmission();

    return () => {
      cancelled = true;
    };
  }, [id]);

  const surveyData = useMemo(() => safeParseSurveyJson(submission?.surveyJson), [submission]);
  const sections = useMemo(() => buildSections(surveyData), [surveyData]);

  const allRows = useMemo(() => {
    return sections.flatMap((sec) =>
      sec.entries.map((entry) => ({ ...entry, section: sec.title }))
    );
  }, [sections]);

  const sectionNames = useMemo(() => ["All", ...sections.map((s) => s.title)], [sections]);

  const filteredRows = useMemo(() => {
    const q = search.toLowerCase();
    return allRows.filter((row) => {
      const matchSection = activeSection === "All" || row.section === activeSection;
      const matchSearch = !q || row.label.toLowerCase().includes(q) || row.value.toLowerCase().includes(q);
      return matchSection && matchSearch;
    });
  }, [allRows, activeSection, search]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const downloadCsv = () => {
    if (!filteredRows.length) return;
    const headers = ["Section", "Field", "Value"];
    const rows = filteredRows.map((r) => [r.section, r.label, r.value].map((v) => `"${String(v ?? "").replaceAll('"', '""')}"`));
    const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `survey-result-${id}.csv`;
    link.click();
  };

  return (
    <div className="page-dashboard" style={{ minHeight: "100vh" }}>
      <DashboardSidebar onLogout={handleLogout} />

      <div className="dash-body">
        <nav className="dash-topbar">
          <div className="topbar-left">
            <h2 className="topbar-title">Survey Result #{id}</h2>
            <span className="topbar-greeting">
              {submission
                ? `${submission.serviceName || "General Survey"} · Submitted by ${submission.submittedBy || "N/A"} on ${submission.submittedOn || "N/A"}`
                : "Loading submission details…"}
            </span>
          </div>
          <div className="topbar-right" style={{ gap: 8, display: "flex" }}>
            <Link to="/services" className="btn-outline-action btn-sm">
              ← All Results
            </Link>
            <button className="btn-outline-action btn-sm" onClick={downloadCsv} disabled={!filteredRows.length}>
              Download CSV
            </button>
          </div>
        </nav>

        {loading && <div className="dash-loading"><div className="spinner"></div>Loading submission…</div>}
        {!loading && error && <div className="form-alert alert-error" style={{ margin: "24px 0" }}>{error}</div>}

        {!loading && !error && submission && (
          <div className="dash-content fade-in">
            <div className="dash-card">
              <div className="dash-table-header">
                <h5>{filteredRows.length} answer{filteredRows.length !== 1 ? "s" : ""}</h5>
                <div className="dash-table-controls">
                  <select
                    value={activeSection}
                    onChange={(e) => setActiveSection(e.target.value)}
                    className="dash-page-size"
                    style={{ minWidth: 200 }}
                  >
                    {sectionNames.map((name) => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    placeholder="Search field or value…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="dash-search"
                  />
                </div>
              </div>

              <div className="table-responsive">
                <table className="premium-table">
                  <thead>
                    <tr>
                      <th>Section</th>
                      <th>Field</th>
                      <th>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.length === 0 ? (
                      <tr><td colSpan={3} className="text-center text-muted">No matching answers</td></tr>
                    ) : (
                      filteredRows.map((row) => (
                        <tr key={row.key}>
                          <td style={{ whiteSpace: "nowrap", color: "var(--muted)", fontSize: 13 }}>{row.section}</td>
                          <td style={{ fontWeight: 500 }}>{row.label}</td>
                          <td style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{row.value}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}